import React from 'react';
import {
  compose,
  withStateHandlers,
  lifecycle,
  StateHandler,
  StateHandlerMap,
} from 'recompose';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { isEqual } from 'lodash';
import { withRouter } from 'react-router-dom';
import campaignsCRUDActions from '@mm/state/actions/CampaignsCRUDActions';
import { StoreState } from '@mm/state/store-interfaces';
import ChatBlastReportCommon from '../../components/ChatBlastReportCommon/ChatBlastReportCommon';
import ChatBlastReportStatistics from '../../components/ChatBlastReportStatistics/ChatBlastReportStatistics';
import ChatBlastReportRecipients from '../../components/ChatBlastReportRecipients/ChatBlastReportRecipients';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { CampaignType } from '@mm/state/model/CampaignsModel';
import { LifeCycleThis } from '@mm/utils';
import './CampaignReportPanel.scss';
import { campaignsWithMappedPurposeSelector } from '@mm/state/selectors/campaignSelectors';

type OuterProps = RouterComponentProps;

type StateProps = {
  selectedCampaign: CampaignType;
  botId: number;
  audienceId: number;
  campaignId: number;
};

type StateUpdatersProps = StateHandlerMap<StateProps> & {
  setSelectedCampaign(value: any): StateHandler<StateProps>;
  setAudienceId(value: number): StateHandler<StateProps>;
};

type MapStateToPropsType = ReturnType<typeof mapStateToProps>;
type MapDispatchToPropsType = ReturnType<typeof mapDispatchToProps>;

type InnerProps = StateProps &
  StateUpdatersProps &
  MapStateToPropsType &
  MapDispatchToPropsType;

type Props = OuterProps & InnerProps;

const initialState = props => {
  const { match } = props;
  return {
    selectedCampaign: null,
    botId: Number(match.params.chatbotId),
    audienceId: null,
    campaignId: Number(match.params.campaignId),
  };
};

const stateUpdaters = {
  setSelectedCampaign: () => selectedCampaign => ({ selectedCampaign }),
  setAudienceId: () => audienceId => ({ audienceId }),
};

const mapStateToProps = (state: StoreState) => ({
  campaigns: campaignsWithMappedPurposeSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getCampaigns: botId =>
    dispatch(
      campaignsCRUDActions.retrieveAll({
        botId,
        pageNumber: 1,
        pageSize: 999999,
        dripCampaign: false,
        withSchedulersAttributes: true,
      }),
    ),
});

function componentDidMount(this: LifeCycleThis<Props>) {
  const { botId } = this.props;
  this.props.getCampaigns(botId);
}

function componentDidUpdate(this: LifeCycleThis<Props>, prevProps) {
  const {
    campaigns,
    setSelectedCampaign,
    setAudienceId,
    match: {
      params: { campaignId },
    },
  } = this.props;
  const { campaigns: prevCampaigns } = prevProps;

  if (!isEqual(prevCampaigns, campaigns)) {
    const campaign = campaigns.find(
      campaign => campaign.id === Number(campaignId),
    );
    if (campaign) {
      setSelectedCampaign(campaign);
      if (campaign.audienceId) {
        setAudienceId(campaign.audienceId);
      }
    }
  }
}

const enhance = compose<InnerProps, OuterProps>(
  withRouter,
  withStateHandlers(initialState, stateUpdaters),
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle<Props, {}>({ componentDidMount, componentDidUpdate }),
);

const CampaignReportPanel = ({ selectedCampaign, botId }: Props) => {
  return (
    selectedCampaign && (
      <div className="blast-report">
        <ChatBlastReportCommon botId={botId} blast={selectedCampaign} />
        <ChatBlastReportStatistics blast={selectedCampaign} />
        <ChatBlastReportRecipients campaignId={selectedCampaign.id} />
      </div>
    )
  );
};

export default enhance(CampaignReportPanel);
